// src/components/home/Hero.js - Version 2: Split Layout with Side Image
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Star, Shield, Truck, Award, Heart } from 'lucide-react';

function Hero({ isMobile, setCurrentPage }) {
  const butcheryImage = "https://www.sirhenrybutchery.com/server/uploads/products/butchery.jpg";

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-black to-amber-900/20">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(to right, #fbbf24 1px, transparent 1px),
                             linear-gradient(to bottom, #fbbf24 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
            initial={{ 
              y: Math.random() * 100 + 'vh',
              x: Math.random() * 100 + 'vw',
              scale: 0
            }}
            animate={{ 
              y: '-100vh',
              x: Math.random() * 100 + 'vw',
              scale: [0, 1, 0]
            }}
            transition={{
              duration: 15 + Math.random() * 15,
              repeat: Infinity,
              ease: 'linear',
              delay: Math.random() * 10,
            }}
          />
        ))}
      </div>

      {/* Glowing Accents */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-r from-amber-500/20 to-orange-600/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`flex flex-col lg:flex-row items-center justify-between gap-12 ${isMobile ? 'pt-12' : ''}`}>
          {/* Left Column: Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 text-center lg:text-left"
          >
            {/* Premium Tag */}
            <div className="inline-flex items-center space-x-2 mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 px-4 py-2 rounded-full border border-amber-500/30">
              <Sparkles className="w-4 h-4 text-amber-300" />
              <span className="text-amber-200 font-bold text-sm">EST. 2025</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-300 to-yellow-200">
                Premium
              </span>
              <span className="block text-white">Butchery</span>
              <span className="block text-amber-400 text-2xl md:text-3xl mt-2">
                Quality You Can Taste
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed">
              From 2025, Sir Henry Butchery has been delivering 
              <span className="text-amber-300 font-semibold"> premium quality meats </span> 
              to families and restaurants across the region. 
              Experience the difference that fresh, carefully selected cuts make.
            </p>
            
            {/* Features */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: <Shield className="w-5 h-5" />, text: "Quality Guarantee", color: "text-green-400" },
                { icon: <Truck className="w-5 h-5" />, text: "Fast Delivery", color: "text-blue-400" },
                { icon: <Award className="w-5 h-5" />, text: "Award Winning", color: "text-yellow-400" },
                { icon: <Heart className="w-5 h-5" />, text: "Family Owned", color: "text-red-400" },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <div className={`${feature.color}`}>{feature.icon}</div>
                  <span className="text-gray-300 font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage('shop')}
                className="group relative px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full font-bold text-lg shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative flex items-center justify-center space-x-2">
                  <span>Shop Now</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
              
              <button
                onClick={() => setCurrentPage('home')}
                className="px-8 py-4 border-2 border-amber-500 text-amber-400 rounded-full font-bold text-lg hover:bg-amber-500/10 transition-all duration-300"
              hidden>
                Learn More
              </button>
            </div>
          </motion.div>

          {/* Right Column: Image Display */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: 90 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="lg:w-1/2"
          >
            <div className="relative group">
              {/* Main Image Frame */}
              <div className="relative overflow-hidden rounded-3xl shadow-2xl shadow-black/50 border-4 border-amber-500/30">
                <motion.img
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.5 }}
                  src={butcheryImage}
                  alt="Sir Henry Butchery"
                  className="w-full h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                
                {/* Image Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute top-6 right-6 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center space-x-2"
                >
                  <Star className="w-4 h-4" />
                  <span>VISIT US</span>
                </motion.div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-gradient-to-r from-red-500/30 to-pink-500/30 rounded-3xl -z-10"></div>
              
              {/* Stats Overlay */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-gray-900 to-black border border-gray-800 rounded-2xl p-4 shadow-xl w-3/4">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: "Serve", label: "With Love" },
                    { value: "Happy", label: "Customers" },
                    { value: "100%", label: "Fresh" }
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-2xl font-bold text-amber-400">{stat.value}</div>
                      <div className="text-xs text-gray-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Location Info */}
            <div className="mt-16 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-gray-900/50 backdrop-blur-sm px-6 py-3 rounded-full border border-gray-700">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-gray-300">📍 Zvido Business Centre, Chitungwiza</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="flex flex-col items-center">
            <span className="text-amber-300 text-sm mb-2">Explore More</span>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-6 h-10 border-2 border-amber-500/50 rounded-full flex justify-center"
            >
              <div className="w-1 h-3 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full mt-2"></div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Hero;